import React, { useId, useState } from 'react';
import { useTranslation } from '../../providers/TranslationProvider';
import { Button } from '../forms/Button';
import { FormField } from '../forms/FormField';
import { Input } from '../forms/Input';
import { TextArea } from '../forms/TextArea';
import { Modal } from '../overlays/Modal';

export interface PromptDialogProps {
	isOpen: boolean;
	/** What is being asked for, e.g. "Reject purchase order". */
	title: string;
	/** Optional context under the title. */
	message?: string;
	/** The field label. */
	label: string;
	/** The value the field starts with. Read once, when the dialog mounts. */
	defaultValue?: string;
	placeholder?: string;
	/** The submit button label. Defaults to "OK". */
	confirmText?: string;
	cancelText?: string;
	/** Submit stays disabled while the value is empty or only whitespace. */
	required?: boolean;
	/** A multi-line text area instead of a one-line input (Enter adds a line). */
	multiline?: boolean;
	/** The one-line input's type: `url` brings up the URL keyboard on phones. */
	inputType?: 'text' | 'url';
	/** Called with the value exactly as typed; trimming is the caller's choice. */
	onSubmit: (value: string) => void;
	/** Cancel, the close button, Escape and a backdrop click all land here. */
	onCancel: () => void;
}

/**
 * Asks for one short text value (PD-15134), replacing `window.prompt`.
 *
 * Built on the shared Modal, so it stacks above whatever overlay opened it and
 * Escape or a backdrop click cancels. Most screens should not render this
 * directly: `usePromptDialog` turns it into an awaitable request, which keeps a
 * call site as short as the `window.prompt` it replaces.
 *
 * The field sits in its own `<form>` so Enter submits a one-line value. The
 * dialog is portalled to <body>, but React still bubbles its submit event up
 * the component tree, into any form the caller lives in (a rich text editor
 * inside an email form, for one). `handleSubmit` therefore stops it, or
 * answering this dialog would also submit the caller's form.
 */
export function PromptDialog({
	isOpen,
	title,
	message,
	label,
	defaultValue,
	placeholder,
	confirmText,
	cancelText,
	required = false,
	multiline = false,
	inputType = 'text',
	onSubmit,
	onCancel,
}: PromptDialogProps): React.ReactElement {
	const { t } = useTranslation();
	const [value, setValue] = useState(defaultValue ?? '');
	const formId = useId();
	const fieldName = useId();
	const isMissingRequiredValue = required && value.trim() === '';

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
		event.preventDefault();
		event.stopPropagation();
		if (isMissingRequiredValue) {
			return;
		}
		onSubmit(value);
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={onCancel}
			title={title}
			description={message}
			size="sm"
			footer={
				<div className="flex justify-end gap-3">
					<Button type="button" variant="outline" onClick={onCancel}>
						{cancelText ?? t('Cancel')}
					</Button>
					{/* Outside the form in the DOM, so it names the form it submits. */}
					<Button
						type="submit"
						form={formId}
						variant="primary"
						disabled={isMissingRequiredValue}
					>
						{confirmText ?? t('OK')}
					</Button>
				</div>
			}
		>
			{/* noValidate: the browser's own URL check would refuse "example.com",
			    which the link prompt has always accepted. */}
			<form id={formId} onSubmit={handleSubmit} noValidate>
				<FormField name={fieldName} label={label} required={required}>
					{multiline ? (
						<TextArea
							value={value}
							onChange={(event) => {
								setValue(event.target.value);
							}}
							placeholder={placeholder}
							rows={4}
							autoFocus
							data-autofocus
						/>
					) : (
						<Input
							type={inputType}
							value={value}
							onChange={(event) => {
								setValue(event.target.value);
							}}
							placeholder={placeholder}
							autoFocus
							data-autofocus
						/>
					)}
				</FormField>
			</form>
		</Modal>
	);
}
